import {
    fetchAllCategories, createCategory, updateCategory, deleteCategory,
    fetchTagsByCategoryId, createTag, updateTag, deleteTag
} from '../../api/categories.js';
import { showToast } from '../../utils/toast.js';

// State machine: 'type' | 'categories' | 'tags'
let _view = 'type';
let _filterType = 'expense';       // 'expense' | 'income'
let _currentCategory = null;       // { id, name }
let _allCategories = [];           // Cache
let _currentTags = [];             // Cache
let _renameTarget = null;          // { type: 'category'|'tag', id, name }

export function initCategoryManager() {
    const modal = document.getElementById('category-manager-modal');
    const content = document.getElementById('category-manager-content');
    const btnOpen = document.getElementById('btn-settings-manage-cats');
    const btnClose = document.getElementById('btn-close-category-manager');
    const btnBack = document.getElementById('btn-catmgr-back');
    const btnAddNew = document.getElementById('btn-catmgr-add-new');

    // Rename bar
    const renameBar = document.getElementById('catmgr-rename-bar');
    const renameInput = document.getElementById('catmgr-rename-input');
    const btnRenameOk = document.getElementById('btn-catmgr-rename-ok');
    const btnRenameCancel = document.getElementById('btn-catmgr-rename-cancel');

    // Add bar
    const addBar = document.getElementById('catmgr-add-bar');
    const addInput = document.getElementById('catmgr-add-input');
    const addError = document.getElementById('catmgr-add-error');
    const btnAddOk = document.getElementById('btn-catmgr-add-ok');
    const btnAddCancel = document.getElementById('btn-catmgr-add-cancel');

    if (!btnOpen) return;

    function openModal() {
        _view = 'type';
        _allCategories = [];
        modal.classList.remove('hidden');
        modal.classList.add('flex');
        setTimeout(() => { content.classList.remove('translate-y-full'); content.classList.add('translate-y-0'); }, 10);
        renderView();
    }

    btnOpen.addEventListener('click', openModal);

    // ---- Rename ---- (delegates to module-level showRenameBar)
    btnRenameOk.addEventListener('click', async () => {
        const newName = renameInput.value.trim();
        if (!newName) return;
        if (_renameTarget && _renameTarget.type === 'category') {
            const ok = await updateCategory(_renameTarget.id, newName);
            if (ok) { showToast('Category renamed!', 'success'); _allCategories = []; hideRenameBar(); renderCategoryList(); if (window.refreshAllActiveViews) window.refreshAllActiveViews(); }
            else showToast('Failed to rename', 'error');
        } else if (_renameTarget) {
            const ok = await updateTag(_renameTarget.id, newName);
            if (ok) { showToast('Tag renamed!', 'success'); hideRenameBar(); renderTagList(_currentCategory.id, _currentCategory.name); if (window.refreshAllActiveViews) window.refreshAllActiveViews(); }
            else showToast('Failed to rename', 'error');
        }
    });
    btnRenameCancel.addEventListener('click', () => hideRenameBar());

    // ---- Add New ----
    function showAddBar() {
        addInput.value = '';
        addError.classList.add('hidden');
        addBar.classList.remove('hidden');
        addInput.focus();
    }
    function hideAddBar() {
        addBar.classList.add('hidden');
        addInput.value = '';
        addError.classList.add('hidden');
    }

    function fullClose() {
        content.classList.remove('translate-y-0');
        content.classList.add('translate-y-full');
        hideAddBar();
        hideRenameBar();
        setTimeout(() => { modal.classList.add('hidden'); modal.classList.remove('flex'); }, 300);
    }

    btnClose.addEventListener('click', fullClose);
    modal.addEventListener('click', (e) => { if (e.target === modal) fullClose(); });

    btnBack.addEventListener('click', () => {
        hideRenameBar();
        hideAddBar();
        if (_view === 'tags') {
            _view = 'categories';
            renderCategoryList();
        } else if (_view === 'categories') {
            _view = 'type';
            renderView();
        }
    });

    btnAddNew.addEventListener('click', showAddBar);
    btnAddCancel.addEventListener('click', hideAddBar);

    btnAddOk.addEventListener('click', async () => {
        const name = addInput.value.trim();
        if (!name) return;
        addError.classList.add('hidden');

        if (_view === 'categories') {
            const exists = _allCategories.some(c => c.name.toLowerCase() === name.toLowerCase() && c.type === _filterType);
            if (exists) { addError.innerText = 'Category already exists!'; addError.classList.remove('hidden'); return; }
            const created = await createCategory(name, _filterType);
            if (created) { showToast('Category created!', 'success'); _allCategories = []; hideAddBar(); renderCategoryList(); if (window.refreshAllActiveViews) window.refreshAllActiveViews(); }
        } else if (_view === 'tags') {
            const exists = _currentTags.some(t => t.name.toLowerCase() === name.toLowerCase());
            if (exists) { addError.innerText = 'Tag already exists!'; addError.classList.remove('hidden'); return; }
            const created = await createTag(name, _currentCategory.id);
            if (created) { showToast('Tag created!', 'success'); hideAddBar(); renderTagList(_currentCategory.id, _currentCategory.name); if (window.refreshAllActiveViews) window.refreshAllActiveViews(); }
        }
    });

    addInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') btnAddOk.click(); });
    renameInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') btnRenameOk.click(); });
}

function getElements() {
    return {
        title: document.getElementById('catmgr-title'),
        backBtn: document.getElementById('btn-catmgr-back'),
        addNewBtn: document.getElementById('btn-catmgr-add-new'),
        footerLabel: document.getElementById('catmgr-footer-label'),
        list: document.getElementById('catmgr-list'),
    };
}

async function renderView() {
    if (_view === 'type') renderTypePicker();
    else if (_view === 'categories') renderCategoryList();
    else if (_view === 'tags') renderTagList(_currentCategory.id, _currentCategory.name);
}

function renderTypePicker() {
    const { title, backBtn, addNewBtn, footerLabel, list } = getElements();
    title.innerText = 'Categories & Tags';
    backBtn.classList.add('hidden');
    addNewBtn.classList.add('hidden');
    footerLabel.innerText = 'Select type to manage';

    list.innerHTML = `
        <div class="p-4 space-y-3">
            <button data-type="expense" class="type-pick-btn w-full p-4 rounded-2xl bg-darkbg border border-gray-700 flex justify-between items-center hover:border-expense transition">
                <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-xl bg-expense bg-opacity-20 flex items-center justify-center">
                        <i class="fa-solid fa-arrow-down text-expense"></i>
                    </div>
                    <span class="text-white font-bold text-base">Expense Categories</span>
                </div>
                <i class="fa-solid fa-chevron-right text-gray-600 text-sm"></i>
            </button>
            <button data-type="income" class="type-pick-btn w-full p-4 rounded-2xl bg-darkbg border border-gray-700 flex justify-between items-center hover:border-income transition">
                <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-xl bg-income bg-opacity-20 flex items-center justify-center">
                        <i class="fa-solid fa-arrow-up text-income"></i>
                    </div>
                    <span class="text-white font-bold text-base">Income Categories</span>
                </div>
                <i class="fa-solid fa-chevron-right text-gray-600 text-sm"></i>
            </button>
        </div>
    `;

    list.querySelectorAll('.type-pick-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            _filterType = btn.getAttribute('data-type');
            _view = 'categories';
            renderCategoryList();
        });
    });
}

async function renderCategoryList() {
    _view = 'categories';
    const { title, backBtn, addNewBtn, footerLabel, list } = getElements();
    const typeLabel = _filterType === 'expense' ? 'Expense' : 'Income';
    title.innerText = `${typeLabel} Categories`;
    backBtn.classList.remove('hidden');
    addNewBtn.classList.remove('hidden');
    footerLabel.innerText = '';

    list.innerHTML = '<div class="flex justify-center p-8"><i class="fa-solid fa-circle-notch fa-spin text-gray-500 text-2xl"></i></div>';

    if (_allCategories.length === 0) {
        _allCategories = await fetchAllCategories();
    }
    const cats = _allCategories.filter(c => c.type === _filterType);

    if (cats.length === 0) {
        list.innerHTML = `<div class="text-center text-gray-500 py-12"><i class="fa-solid fa-folder-open text-3xl mb-3 block"></i>No categories yet. Tap "Add New" to create one.</div>`;
        return;
    }

    list.innerHTML = '';
    const colorClass = _filterType === 'expense' ? 'text-expense' : 'text-income';
    const bgClass = _filterType === 'expense' ? 'bg-expense' : 'bg-income';

    cats.forEach(cat => {
        const tagCount = cat.tags ? (Array.isArray(cat.tags) ? cat.tags.length : (cat.tags[0]?.count ?? 0)) : 0;
        const div = document.createElement('div');
        div.className = 'flex items-center px-5 py-3.5 border-b border-gray-800 gap-3';
        div.innerHTML = `
            <div class="flex-1 min-w-0">
                <div class="text-white font-medium text-sm truncate" id="cat-name-${cat.id}">${cat.name}</div>
                <div class="text-gray-500 text-xs">${tagCount} tag${tagCount !== 1 ? 's' : ''}</div>
            </div>
            <button class="btn-cat-rename p-2 text-gray-400 hover:text-primary text-sm transition rounded-lg hover:bg-gray-800" data-id="${cat.id}" data-name="${cat.name}" title="Rename">
                <i class="fa-solid fa-pen-to-square"></i>
            </button>
            <button class="btn-cat-delete p-2 text-gray-400 hover:text-expense text-sm transition rounded-lg hover:bg-gray-800" data-id="${cat.id}" data-name="${cat.name}" title="Delete">
                <i class="fa-solid fa-trash-can"></i>
            </button>
            <button class="btn-cat-tags flex items-center gap-1 px-3 py-1.5 text-xs ${colorClass} border border-current rounded-lg hover:bg-gray-800 transition" data-id="${cat.id}" data-name="${cat.name}">
                Tags <i class="fa-solid fa-chevron-right text-[10px]"></i>
            </button>
        `;

        div.querySelector('.btn-cat-rename').addEventListener('click', () => {
            document.getElementById('btn-catmgr-add-new').dispatchEvent = null; // ensure no open add bar
            document.getElementById('catmgr-add-bar').classList.add('hidden');
            showRenameBar({ type: 'category', id: cat.id, name: cat.name });
        });

        div.querySelector('.btn-cat-delete').addEventListener('click', async () => {
            const confirmed = confirm(`Delete category "${cat.name}"? All associated tags will also be deleted. Historical transactions will keep the reference.`);
            if (!confirmed) return;
            const ok = await deleteCategory(cat.id);
            if (ok) { showToast(`Category "${cat.name}" deleted`, 'success'); _allCategories = []; renderCategoryList(); if (window.refreshAllActiveViews) window.refreshAllActiveViews(); }
            else showToast('Failed to delete', 'error');
        });

        div.querySelector('.btn-cat-tags').addEventListener('click', () => {
            _currentCategory = { id: cat.id, name: cat.name };
            _view = 'tags';
            renderTagList(cat.id, cat.name);
        });

        list.appendChild(div);
    });
}

function showRenameBar(target) {
    const renameBar = document.getElementById('catmgr-rename-bar');
    const renameInput = document.getElementById('catmgr-rename-input');
    document.getElementById('catmgr-add-bar').classList.add('hidden');
    _renameTarget = target;
    renameInput.value = target.name;
    renameBar.classList.remove('hidden');
    renameInput.focus();
}

function hideRenameBar() {
    _renameTarget = null;
    document.getElementById('catmgr-rename-bar').classList.add('hidden');
    document.getElementById('catmgr-rename-input').value = '';
}

async function renderTagList(categoryId, categoryName) {
    _view = 'tags';
    const { title, backBtn, addNewBtn, footerLabel, list } = getElements();
    title.innerText = categoryName;
    backBtn.classList.remove('hidden');
    addNewBtn.classList.remove('hidden');
    footerLabel.innerText = 'Tags';

    list.innerHTML = '<div class="flex justify-center p-8"><i class="fa-solid fa-circle-notch fa-spin text-gray-500 text-2xl"></i></div>';

    _currentTags = await fetchTagsByCategoryId(categoryId);

    if (_currentTags.length === 0) {
        list.innerHTML = `<div class="text-center text-gray-500 py-12"><i class="fa-solid fa-tag text-3xl mb-3 block"></i>No tags yet. Tap "Add New" to create one.</div>`;
        return;
    }

    list.innerHTML = '';
    _currentTags.forEach(tag => {
        const div = document.createElement('div');
        div.className = 'flex items-center px-5 py-3.5 border-b border-gray-800 gap-3';
        div.innerHTML = `
            <div class="flex-1 min-w-0">
                <div class="text-white font-medium text-sm truncate">${tag.name}</div>
            </div>
            <button class="btn-tag-rename p-2 text-gray-400 hover:text-primary text-sm transition rounded-lg hover:bg-gray-800" data-id="${tag.id}" data-name="${tag.name}" title="Rename">
                <i class="fa-solid fa-pen-to-square"></i>
            </button>
            <button class="btn-tag-delete p-2 text-gray-400 hover:text-expense text-sm transition rounded-lg hover:bg-gray-800" data-id="${tag.id}" data-name="${tag.name}" title="Delete">
                <i class="fa-solid fa-trash-can"></i>
            </button>
        `;

        div.querySelector('.btn-tag-rename').addEventListener('click', () => {
            document.getElementById('catmgr-add-bar').classList.add('hidden');
            showRenameBar({ type: 'tag', id: tag.id, name: tag.name });
        });

        div.querySelector('.btn-tag-delete').addEventListener('click', async () => {
            const confirmed = confirm(`Delete tag "${tag.name}"?`);
            if (!confirmed) return;
            const ok = await deleteTag(tag.id);
            if (ok) { showToast(`Tag "${tag.name}" deleted`, 'success'); renderTagList(categoryId, categoryName); if (window.refreshAllActiveViews) window.refreshAllActiveViews(); }
            else showToast('Failed to delete', 'error');
        });

        list.appendChild(div);
    });
}
